import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";

export default class PatentCompoundStore {
  compounds = [];
  columns = [];
  groups = [];
  selectedGroupId = null;
  isUngrouped = false;
  loading = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  selectGroup(groupId) {
    this.selectedGroupId = groupId;
    this.isUngrouped = false;
  }

  selectUngrouped() {
    this.selectedGroupId = null;
    this.isUngrouped = true;
  }

  // ── Groups ─────────────────────────────────────────────────

  async fetchGroups(projectId) {
    if (!projectId) return;
    try {
      const data = await agent.PatentCompounds.listGroups(projectId);
      runInAction(() => { this.groups = data || []; });
    } catch (err) {
      console.error("Failed to load patent groups:", err);
      runInAction(() => { this.groups = []; });
    }
  }

  async createGroup(projectId, name) {
    try {
      const result = await agent.PatentCompounds.createGroup(projectId, { name });
      if (result !== null) {
        await this.fetchGroups(projectId);
      }
      return result;
    } catch (err) {
      console.error("Failed to create patent group:", err);
      return null;
    }
  }

  async deleteGroup(id, projectId) {
    try {
      const result = await agent.PatentCompounds.deleteGroup(id);
      if (result !== null) {
        if (this.selectedGroupId === id) {
          this.selectedGroupId = null;
        }
        await this.fetchGroups(projectId);
      }
      return result;
    } catch (err) {
      console.error("Failed to delete patent group:", err);
      return null;
    }
  }

  // ── Compounds ──────────────────────────────────────────────

  async fetchCompounds(projectId) {
    if (!projectId) return;
    this.loading = true;
    try {
      const data = await agent.PatentCompounds.list(projectId, this.selectedGroupId, this.isUngrouped);
      runInAction(() => { this.compounds = data || []; });
    } catch (err) {
      console.error("Failed to load patent compounds:", err);
      runInAction(() => { this.compounds = []; });
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  async saveCompound(projectId, compound) {
    try {
      const result = await agent.PatentCompounds.save(projectId, compound);
      if (result !== null) {
        await this.fetchCompounds(projectId);
      }
      return result;
    } catch (err) {
      console.error("Failed to save patent compound:", err);
      return null;
    }
  }

  async deleteCompound(id, projectId) {
    try {
      const result = await agent.PatentCompounds.delete(id);
      if (result !== null) {
        await this.fetchCompounds(projectId);
      }
      return result;
    } catch (err) {
      console.error("Failed to delete patent compound:", err);
      return null;
    }
  }

  // ── Columns ────────────────────────────────────────────────

  async fetchColumns(projectId, groupId) {
    try {
      const data = await agent.PatentCompounds.listColumns(projectId, groupId);
      runInAction(() => { this.columns = data || []; });
    } catch (err) {
      console.error("Failed to load patent columns:", err);
      runInAction(() => { this.columns = []; });
    }
  }

  async saveColumns(projectId, columns) {
    try {
      const result = await agent.PatentCompounds.saveColumns(projectId, { columns });
      if (result !== null) {
        await this.fetchColumns(projectId, this.selectedGroupId);
      }
      return result;
    } catch (err) {
      console.error("Failed to save patent columns:", err);
      return null;
    }
  }

  // ── CSV Import ─────────────────────────────────────────────

  async importCsv(projectId, file) {
    try {
      const result = await agent.PatentCompounds.importCsv(projectId, file, this.selectedGroupId);
      if (result !== null) {
        await this.fetchCompounds(projectId, this.selectedGroupId);
        await this.fetchColumns(projectId, this.selectedGroupId);
      }
      return result;
    } catch (err) {
      console.error("Failed to import CSV:", err);
      return null;
    }
  }
}
